import { useEffect, useState } from "react";
import { useBlocker } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { usePendingEdits } from "@/lib/pending-edits";
import { Button } from "@/components/design/Button";
import { Dialog } from "@/components/common/Dialog";

// Protects against data loss: warns about unsaved inline edits before the
// user navigates (in-app) or closes/reloads the tab. Dirty state comes from
// the central PendingEdits store. Uses the shared Dialog blueprint.

export function UnsavedGuard() {
  const intl = useIntl();
  const ctx = usePendingEdits();
  const dirtyScopes = Object.values(ctx?.scopes ?? {}).filter((s) => s.dirty);
  const hasDirty = dirtyScopes.length > 0;

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Tab close / reload → native browser warning.
  useEffect(() => {
    if (!hasDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasDirty]);

  // In-app navigation blocked while dirty.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  const onStay = () => {
    setError("");
    blocker.reset?.();
  };

  const onDiscard = () => {
    setError("");
    dirtyScopes.forEach((s) => s.discard());
    blocker.proceed?.();
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    try {
      await Promise.all(dirtyScopes.map((s) => s.save()));
      ctx?.signalSaved();
      blocker.proceed?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : intl.formatMessage({ id: "common.saveFailed", defaultMessage: "Save failed." }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={blocker.state === "blocked"}
      onClose={onStay}
      title={
        <FormattedMessage id="unsavedGuard.title" defaultMessage="Unsaved changes" />
      }
    >
      <p className="mb-5 text-[13px] leading-relaxed text-ink/70">
        <FormattedMessage
          id="unsavedGuard.body"
          defaultMessage="You have changes that haven't been saved yet. If you leave this page, they will be lost."
        />
      </p>
      {error ? (
        <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-[12px] text-red-700">{error}</div>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onDiscard} disabled={saving}>
          <FormattedMessage id="unsavedGuard.discard" defaultMessage="Discard" />
        </Button>
        <Button variant="secondary" onClick={onStay} disabled={saving}>
          <FormattedMessage id="unsavedGuard.stay" defaultMessage="Stay here" />
        </Button>
        <Button variant="primary" onClick={() => void onSave()} disabled={saving}>
          {saving ? (
            <FormattedMessage id="common.saving" defaultMessage="Saving…" />
          ) : (
            <FormattedMessage id="unsavedGuard.save" defaultMessage="Save" />
          )}
        </Button>
      </div>
    </Dialog>
  );
}
